import { Loader2 } from "lucide-react"
import type { ComponentProps, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "./button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./dialog"

export type ConfirmationDialogProps = {
	open: boolean
	title?: ReactNode
	description?: ReactNode
	cancelLabel?: ReactNode
	confirmLabel?: ReactNode
	confirmVariant?: ComponentProps<typeof Button>["variant"]
	isPending?: boolean
	onCancel: () => void
	onConfirm: () => void
}

export function ConfirmationDialog({
	open,
	title,
	description,
	cancelLabel,
	confirmLabel,
	confirmVariant = "default",
	isPending = false,
	onCancel,
	onConfirm,
}: ConfirmationDialogProps) {
	const { t } = useTranslation()
	const resolvedTitle = title ?? t("common.confirmation.title")
	const resolvedDescription =
		description ?? t("common.confirmation.description")
	const resolvedCancelLabel = cancelLabel ?? t("common.confirmation.cancel")
	const resolvedConfirmLabel = confirmLabel ?? t("common.confirmation.confirm")

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				if (!nextOpen && !isPending) {
					onCancel()
				}
			}}
		>
			<DialogContent
				showCloseButton={false}
				onEscapeKeyDown={
					isPending ? (event) => event.preventDefault() : undefined
				}
				onPointerDownOutside={
					isPending ? (event) => event.preventDefault() : undefined
				}
			>
				<DialogHeader>
					<DialogTitle>{resolvedTitle}</DialogTitle>
					{resolvedDescription ? (
						<DialogDescription>{resolvedDescription}</DialogDescription>
					) : null}
				</DialogHeader>
				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						disabled={isPending}
						onClick={onCancel}
					>
						{resolvedCancelLabel}
					</Button>
					<Button
						type="button"
						variant={confirmVariant}
						disabled={isPending}
						aria-busy={isPending}
						onClick={onConfirm}
					>
						{isPending ? (
							<Loader2 className="animate-spin" aria-hidden />
						) : null}
						{resolvedConfirmLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
